import React, { useRef, useCallback } from 'react';
import { Bold, Italic, Underline, List, ListOrdered, Heading2, Heading3 } from 'lucide-react';
import { Textarea } from './ui/textarea';

// Toolbar button component
const ToolbarButton = ({ icon: Icon, onClick, title, isActive = false }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className={`p-2 rounded hover:bg-zinc-700 transition-colors ${
      isActive ? 'bg-zinc-700 text-navigate-gold' : 'text-zinc-400 hover:text-white'
    }`}
  >
    <Icon className="h-4 w-4" />
  </button>
);

export default function RichTextEditor({ 
  value, 
  onChange, 
  placeholder = 'Enter content...', 
  minHeight = '150px',
  className = '',
  testId = 'rich-text-editor'
}) {
  const textareaRef = useRef(null);

  // Get the current selection in the textarea
  const getSelection = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return { start: 0, end: 0, text: '' };
    
    return {
      start: textarea.selectionStart,
      end: textarea.selectionEnd,
      text: value.substring(textarea.selectionStart, textarea.selectionEnd)
    };
  }, [value]);

  // Apply formatting to the selected text
  const applyFormat = useCallback((prefix, suffix = prefix, placeholder = 'text') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const { start, end, text } = getSelection();
    const selectedText = text || placeholder;
    
    // Check if the selected text is already formatted
    const beforeSelection = value.substring(0, start);
    const afterSelection = value.substring(end);
    
    // Create the new formatted text
    const formattedText = `${prefix}${selectedText}${suffix}`;
    const newValue = beforeSelection + formattedText + afterSelection;
    
    onChange(newValue);
    
    // Set cursor position after formatting
    setTimeout(() => {
      const newStart = start + prefix.length;
      const newEnd = newStart + selectedText.length;
      textarea.focus();
      textarea.setSelectionRange(newStart, newEnd);
    }, 0);
  }, [value, onChange, getSelection]);

  // Insert text at cursor position
  const insertAtCursor = useCallback((textToInsert) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const { start, end } = getSelection();
    const beforeSelection = value.substring(0, start);
    const afterSelection = value.substring(end);
    
    const newValue = beforeSelection + textToInsert + afterSelection;
    onChange(newValue);
    
    // Set cursor position after the inserted text
    setTimeout(() => {
      const newPosition = start + textToInsert.length;
      textarea.focus();
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
  }, [value, onChange, getSelection]);

  // Insert bullets at the start of the current line
  const insertBullet = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const { start } = getSelection();
    
    // Find the start of the current line
    let lineStart = start;
    while (lineStart > 0 && value[lineStart - 1] !== '\n') {
      lineStart--;
    }
    
    const beforeLine = value.substring(0, lineStart);
    const afterCursor = value.substring(start);
    const currentLineBeforeCursor = value.substring(lineStart, start);
    
    // Check if line already starts with a bullet
    if (currentLineBeforeCursor.trimStart().startsWith('- ')) {
      // Remove the bullet
      const lineWithoutBullet = currentLineBeforeCursor.replace(/^\s*- /, '');
      onChange(beforeLine + lineWithoutBullet + afterCursor);
    } else {
      // Add bullet
      onChange(beforeLine + '- ' + currentLineBeforeCursor + afterCursor);
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + 2, start + 2);
      }, 0);
    }
  }, [value, onChange, getSelection]);

  // Insert numbered list item
  const insertNumberedList = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const { start } = getSelection();
    
    // Find the start of the current line
    let lineStart = start;
    while (lineStart > 0 && value[lineStart - 1] !== '\n') {
      lineStart--;
    }
    
    const beforeLine = value.substring(0, lineStart);
    const afterCursor = value.substring(start);
    const currentLineBeforeCursor = value.substring(lineStart, start);
    
    // Check if line already starts with a number
    const numberedMatch = currentLineBeforeCursor.match(/^\s*\d+\.\s/);
    if (numberedMatch) {
      // Remove the number
      const lineWithoutNumber = currentLineBeforeCursor.replace(/^\s*\d+\.\s/, '');
      onChange(beforeLine + lineWithoutNumber + afterCursor);
    } else {
      // Count existing numbered items above to determine the next number
      const textAbove = beforeLine;
      const lines = textAbove.split('\n');
      let nextNumber = 1;
      
      // Look for the last numbered item
      for (let i = lines.length - 1; i >= 0; i--) {
        const match = lines[i].match(/^\s*(\d+)\./);
        if (match) {
          nextNumber = parseInt(match[1]) + 1;
          break;
        } else if (lines[i].trim() !== '') {
          // Non-empty non-numbered line, reset count
          break;
        }
      }
      
      onChange(beforeLine + `${nextNumber}. ` + currentLineBeforeCursor + afterCursor);
      setTimeout(() => {
        const offset = nextNumber.toString().length + 2;
        textarea.focus();
        textarea.setSelectionRange(start + offset, start + offset);
      }, 0);
    }
  }, [value, onChange, getSelection]);

  // Insert heading
  const insertHeading = useCallback((level) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const { start } = getSelection();
    
    // Find the start of the current line
    let lineStart = start;
    while (lineStart > 0 && value[lineStart - 1] !== '\n') {
      lineStart--;
    }
    
    const beforeLine = value.substring(0, lineStart);
    const afterCursor = value.substring(start);
    const currentLineBeforeCursor = value.substring(lineStart, start);
    
    const prefix = '#'.repeat(level) + ' ';
    
    // Check if line already has this heading level
    const existingMatch = currentLineBeforeCursor.match(/^(#{1,6})\s/);
    if (existingMatch) {
      if (existingMatch[1].length === level) {
        // Remove heading
        const lineWithoutHeading = currentLineBeforeCursor.replace(/^#{1,6}\s/, '');
        onChange(beforeLine + lineWithoutHeading + afterCursor);
      } else {
        // Change heading level
        const lineWithNewHeading = currentLineBeforeCursor.replace(/^#{1,6}\s/, prefix);
        onChange(beforeLine + lineWithNewHeading + afterCursor);
      }
    } else {
      // Add heading
      onChange(beforeLine + prefix + currentLineBeforeCursor + afterCursor);
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + prefix.length, start + prefix.length);
      }, 0);
    }
  }, [value, onChange, getSelection]);

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback((e) => {
    if (e.ctrlKey || e.metaKey) {
      switch (e.key.toLowerCase()) {
        case 'b':
          e.preventDefault();
          applyFormat('**', '**', 'bold text');
          break;
        case 'i':
          e.preventDefault();
          applyFormat('*', '*', 'italic text');
          break;
        case 'u':
          e.preventDefault();
          applyFormat('<u>', '</u>', 'underlined text');
          break;
        default:
          break;
      }
    }
  }, [applyFormat]);

  return (
    <div className={`border border-purple-500/50 rounded-lg overflow-hidden bg-zinc-900 ${className}`}>
      {/* Formatting Toolbar */}
      <div className="flex items-center gap-1 p-2 bg-zinc-800 border-b border-zinc-700">
        <ToolbarButton
          icon={Bold}
          onClick={() => applyFormat('**', '**', 'bold text')}
          title="Bold (Ctrl+B)"
        />
        <ToolbarButton
          icon={Italic}
          onClick={() => applyFormat('*', '*', 'italic text')}
          title="Italic (Ctrl+I)"
        />
        <ToolbarButton
          icon={Underline}
          onClick={() => applyFormat('<u>', '</u>', 'underlined text')}
          title="Underline (Ctrl+U)"
        />
        
        <div className="w-px h-6 bg-zinc-700 mx-1" />
        
        <ToolbarButton
          icon={List}
          onClick={insertBullet}
          title="Bullet List"
        />
        <ToolbarButton
          icon={ListOrdered}
          onClick={insertNumberedList}
          title="Numbered List"
        />
        
        <div className="w-px h-6 bg-zinc-700 mx-1" />
        
        <ToolbarButton
          icon={Heading2}
          onClick={() => insertHeading(2)}
          title="Heading 2"
        />
        <ToolbarButton
          icon={Heading3}
          onClick={() => insertHeading(3)}
          title="Heading 3"
        />
        
        <div className="flex-1" />
        
        <span className="text-xs text-zinc-500 px-2">Markdown</span>
      </div>
      
      {/* Editor Area */}
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onPaste={(e) => e.stopPropagation()}
        placeholder={placeholder}
        className="w-full border-0 bg-transparent text-zinc-300 font-mono text-sm resize-y focus:ring-0 focus:outline-none rounded-none"
        style={{ minHeight }}
        data-testid={testId}
      />
    </div>
  );
}
